import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import lodash from "lodash";

import {PageProps, PaginationTrigger} from "@models/PageProps.ts";

async function processEvent(props: { event: PaginationTrigger, pagination: IPagination }) {
    const promise = props.event.promise({pagination: props.pagination});
    return await promise?.then(x => {
        if (props.event.after != null) {
            props.event.after(x)
        }

        return x
    })
}

interface IPaginationWithEvents extends IPagination {
    events?: PaginationTrigger[],
    deps?: any[]
}


type usePaginationReturn = [IPagination & {
    events?: PaginationTrigger[]
}, Dispatch<SetStateAction<IPaginationWithEvents>>]

/**
 * Hook de paginação com sincronização pai/filho sem setState em render.
 */
export default function usePagination(props: Partial<Omit<PageProps, 'triggers'>> & {
    triggers?: PaginationTrigger[]
} | undefined = undefined, initialState: IPaginationWithEvents | undefined = undefined): usePaginationReturn {
    const [pagination, setPagination] = useState<IPaginationWithEvents>({
        page: 1, pageSize: 10, loading: false,
        ...props?.pagination,
        ...initialState
    });


    // controla a origem para evitar eco
    const sourceRef = useRef<"parent" | "child" | null>(null);
    // agenda propagação ao pai fora do render
    const pendingToParentRef = useRef<IPaginationWithEvents | null>(null);

    const equalToParent = useMemo(() => lodash.isEqual(pagination, props?.pagination), [pagination, props?.pagination]);

    // sync do pai -> filho
    useEffect(() => {
        if (!props?.pagination) return;
        if (!equalToParent) {
            if (sourceRef.current === "child") {
                sourceRef.current = null; // ignora eco
                return;
            }
            sourceRef.current = "parent";
            setPagination(prev => ({...prev, ...props.pagination!}));
            sourceRef.current = null;
        }
    }, [props?.pagination, equalToParent]);

    // propaga do filho -> pai somente em efeito
    useEffect(() => {
        if (pendingToParentRef.current && props?.setPagination != null) {
            const next = pendingToParentRef.current;
            pendingToParentRef.current = null;
            // merge com o valor atual do pai para não perder campos controlados por lá
            const merged = {...props.pagination, ...next} as IPagination;
            if (!lodash.isEqual(merged, props.pagination)) {
                props.setPagination(merged);
            }
        }
    }, [props?.setPagination, props?.pagination, pagination]);

    const triggers = useMemo(() => {
        if (props == null || props.triggers == null || props.triggers.length <= 0) return []

        return props.triggers
    }, [props?.triggers])

    const handleTrigger = async (event: PaginationTrigger) => {
        if (event.options?.setOnLoading === true || event.eventName == 'process') {
            setPagination(prev => ({...prev, loading: true}))
        }

        await processEvent({event, pagination})/*.then(x => {
            setPagination(prev => ({...prev, ...x.pagination}))
        })*/.finally(() => {
            if (event.options?.setOnLoading === true || event.eventName == 'process') {
                setPagination(prev => ({...prev, loading: false}))
            }

            setPagination(prev => ({...prev, ignoreCache: false}))
        })
    }

    useEffect(() => {
            if (triggers.length > 0) {
                triggers?.filter(t => t.eventName == 'pageChange').forEach(handleTrigger)
            }
        },
        [pagination.page])

    useEffect(() => {
            if (triggers.length > 0) {
                triggers?.filter(t => t.eventName == 'pageSizeChange').forEach(handleTrigger)
            }
        },
        [pagination.pageSize])

    useEffect(() => {
            if (triggers.length > 0) {
                triggers?.filter(t => t.eventName == 'process').forEach(handleTrigger)
            }
        },
        [pagination.page, pagination.pageSize, pagination.ignoreCache == true])

    // setter local que agenda propagação ao pai (sem chamar no meio do render)
    const setPaginationSmart = useCallback<Dispatch<SetStateAction<IPaginationWithEvents>>>((updater) => {
        sourceRef.current = "child";
        setPagination(prev => {
            const nextVal = typeof updater === "function" ? (updater as (p: IPaginationWithEvents) => IPaginationWithEvents)(prev) : updater;
            if (!lodash.isEqual(nextVal, prev)) {
                pendingToParentRef.current = nextVal;
            }
            return nextVal;
        });
    }, []);

    return [pagination, setPaginationSmart];
}